// src/components/AdminMobileBottomNav.jsx
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Users, BookOpen, MoreHorizontal, UserCog, Server, Settings, CreditCard, LogOut, Activity } from 'lucide-react';

const AdminMobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Users', path: '/admin/users' },
    { icon: BookOpen, label: 'Books', path: '/admin/books' },
    { icon: MoreHorizontal, label: 'More', action: 'more' },
  ];

  const moreMenuItems = [
    { icon: UserCog, label: 'Creators', path: '/admin/authors' },
    { icon: Server, label: 'System', path: '/admin/system' },
    { icon: Activity, label: 'Diagnostics', path: '/admin/diagnostics' },
    { icon: Settings, label: 'Account', path: '/account' },
    { icon: CreditCard, label: 'Billing', path: '/account' },
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile Bottom Navigation - Only show on screens < 768px */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-slate-800 bg-slate-950/95 backdrop-blur">
        <div className="flex items-center justify-around px-2 py-3">
          {navItems.map((item) => {
            if (item.action === 'more') {
              return (
                <button
                  key="more"
                  onClick={() => setShowMoreMenu(true)}
                  className="flex flex-col items-center gap-1 px-3 py-1 text-slate-400 hover:text-amber-400 transition-colors"
                >
                  <item.icon className="h-5 w-5" />
                  <span className="text-xs font-medium">{item.label}</span>
                </button>
              );
            }

            // Smart hiding: hide current page icon
            if (isActive(item.path)) {
              return null;
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center gap-1 px-3 py-1 text-slate-400 hover:text-amber-400 transition-colors"
              >
                <item.icon className="h-5 w-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* More Menu Slide-up Modal */}
      {showMoreMenu && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 z-[60] bg-black/50"
            onClick={() => setShowMoreMenu(false)}
          />
          
          {/* Slide-up Menu */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-[70] rounded-t-2xl border-t border-slate-700 bg-slate-900 animate-slide-up">
            <div className="px-4 py-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-200">More Options</h3>
                <button
                  onClick={() => setShowMoreMenu(false)}
                  className="text-slate-400 hover:text-slate-200"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-1">
                {moreMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-3 rounded-lg px-4 py-3 text-slate-200 hover:bg-slate-800 transition-colors"
                    onClick={() => setShowMoreMenu(false)}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ))}
                
                <div className="my-2 border-t border-slate-700" />
                
                <button
                  onClick={() => {
                    setShowMoreMenu(false);
                    handleLogout();
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-red-400 hover:bg-slate-800 transition-colors"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AdminMobileBottomNav;
